import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Button, Input, PhoneInput } from "@unified-checkout/design-system";
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { useEnterKeyPress } from "@app/dashboard/hooks/useEnterKeyPress";
import { userActivationDataSubmit } from "@app/dashboard/services/outer_layout/user-activation";
import type { IUserActivationForm } from '@app/dashboard/types/outerLayout/user-activation';
import {
  isFormValid,
  passwordValidationRules
} from '@app/dashboard/utils/formValidations';
import { iso3ToIso2 } from '@app/dashboard/utils/countriesUtils';


const SUBMIT_BTN_ID = 'user-activation-form-submit-btn';


const UserActivationForm = ({
  validateField,
  loading,
  setLoading,
  redirectToLogin,
  setCurrentStep,
  STEPS,
  country,
  setSubmissionResponseData,
  fields,
  setFields,
  FieldNames,
  LOGIN_REDIRECT_TIMEOUT,
  isInvitedBy,
  token
}:
  IUserActivationForm
) => {
  const { t } = useTranslation();
  useEnterKeyPress(SUBMIT_BTN_ID);

  const countryIso2 = iso3ToIso2(country);

  const [serverErrMsg, setServerErrMsg] = useState('');
  const [showPasswordRules, setShowPasswordRules] = useState(false);

  useEffect(() => {
    const passwordValue = fields[FieldNames.PASSWORD].value;
    const confirmPasswordValue = fields[FieldNames.CONFIRM_PASSWORD].value;

    typeof passwordValue === 'string' &&
      (
        validateField(fields, setFields, FieldNames.PASSWORD, passwordValue),
        setShowPasswordRules(true)
      );

    typeof confirmPasswordValue === 'string' && validateField(fields, setFields, FieldNames.CONFIRM_PASSWORD, confirmPasswordValue);

  }, [fields[FieldNames.PASSWORD].value]);

  useEffect(() => {
    const confirmPasswordValue = fields[FieldNames.CONFIRM_PASSWORD].value;
    typeof confirmPasswordValue === 'string' && validateField(fields, setFields, FieldNames.CONFIRM_PASSWORD, confirmPasswordValue);

  }, [fields[FieldNames.CONFIRM_PASSWORD].value]);

  useEffect(() => {

    const fieldsToValidate = ['EMAIL', 'PHONE_NUMBER'] as ('EMAIL' | 'PHONE_NUMBER')[];

    fieldsToValidate.forEach((field) =>
      fields[FieldNames[field]].value &&
      validateField(fields, setFields, FieldNames[field], fields[FieldNames[field]].value as string)
    )

  }, [fields[FieldNames.EMAIL].value, fields[FieldNames.PHONE_NUMBER].value]);


  const handlePhoneNumberChange = (number: string) => {
    const numWithoutPhoneCountryCode = number.replace(COUNTRIES_INFO[country].phone_country_code, '');

    onChangeHandler(FieldNames.PHONE_NUMBER, numWithoutPhoneCountryCode);
  };

  const onChangeHandler = (fieldName: string, value: string) => {
    setServerErrMsg("");

    setFields(prevState => {
      return ({
        ...prevState,
        [fieldName]: {
          ...prevState[fieldName],
          value: value
        }
      })
    });

    validateField(fields, setFields, fieldName, value);
  };

  const handleSubmit = () => {
    setLoading(true);
    setServerErrMsg("");

    const phone = fields[FieldNames.PHONE_NUMBER].value;

    const args = {
      token,
      userNameType: isInvitedBy,
      fullName: fields[FieldNames.FULL_NAME].value as string,
      email: fields[FieldNames.EMAIL].value as string,
      ...(phone ? { phone } : {}),
      password: fields[FieldNames.PASSWORD].value as string
    };

    userActivationDataSubmit(args)
      .then((response) => {
        const {
          verified,
          status,
          token,
          phone,
          email,
          otp_send_to
        } = response?.data;

        if (verified) {
          setCurrentStep(STEPS.SUCCESSFUL_ACTIVATION);

          setTimeout(() => {
            redirectToLogin();
          }, LOGIN_REDIRECT_TIMEOUT);

        } else if (status && otp_send_to && token) {
          setSubmissionResponseData({
            otpToken: token,
            phone,
            email
          });
          setCurrentStep(STEPS.OTP);
        }
      })
      .catch((error) => {
        const data = error?.response?.data;
        const message = data?.message;
        const status = data?.status;

        status === false && setServerErrMsg(message || t('errors.somethingWentWrong'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const emailAndPhoneInputs = [
    <Input
      key={FieldNames.EMAIL}
      id={FieldNames.EMAIL}
      name={FieldNames.EMAIL}
      type='email'
      label={t('form.email')}
      labelText={isInvitedBy === 'phone' ? `(${t('outerLayout.form.labels.optional')})` : ''}
      value={fields[FieldNames.EMAIL].value}
      onChange={(e) => onChangeHandler(FieldNames.EMAIL, e.target.value)}
      status={fields[FieldNames.EMAIL].isValid === false ? 'error' : 'default'}
      error={fields[FieldNames.EMAIL].errorMsg}
      disabled={loading || isInvitedBy === 'email'}
      preventAutoComplete
    />,
    <PhoneInput
      key={FieldNames.PHONE_NUMBER}
      id={FieldNames.PHONE_NUMBER}
      name={FieldNames.PHONE_NUMBER}
      label={t('form.phoneNumber')}
      labelHint={isInvitedBy === 'email' ? `(${t('outerLayout.form.labels.optional')})` : ''}
      disableSelectCountry
      phoneNumber={fields[FieldNames.PHONE_NUMBER].value}
      onChangeHandler={handlePhoneNumberChange}
      country={countryIso2}
      error={fields[FieldNames.PHONE_NUMBER].errorMsg}
      disabled={loading || isInvitedBy === 'phone'}
    />
  ];

  return (
    <>
      <Input
        id={FieldNames.FULL_NAME}
        name={FieldNames.FULL_NAME}
        type='text'
        label={t('form.fullName')}
        value={fields[FieldNames.FULL_NAME].value}
        onChange={(e) => onChangeHandler(FieldNames.FULL_NAME, e.target.value)}
        status={fields[FieldNames.FULL_NAME].isValid === false ? 'error' : 'default'}
        error={fields[FieldNames.FULL_NAME].errorMsg}
        preventAutoComplete
        required
      />

      {isInvitedBy === 'email' ? emailAndPhoneInputs : [...emailAndPhoneInputs].reverse()}

      <Input
        id={FieldNames.PASSWORD}
        name={FieldNames.PASSWORD}
        type='password'
        label={t('outerLayout.form.labels.password')}
        value={fields[FieldNames.PASSWORD].value}
        {...(showPasswordRules ? { rules: passwordValidationRules } : {})}
        onChange={(e) => onChangeHandler(FieldNames.PASSWORD, e.target.value)}
        status={fields[FieldNames.PASSWORD].isValid === false ? 'error' : 'default'}
        preventAutoComplete
        disabled={loading}
      />
      <Input
        id={FieldNames.CONFIRM_PASSWORD}
        name={FieldNames.CONFIRM_PASSWORD}
        type='password'
        label={t('outerLayout.form.labels.confirmPassword')}
        value={fields[FieldNames.CONFIRM_PASSWORD].value}
        onChange={(e) => onChangeHandler(FieldNames.CONFIRM_PASSWORD, e.target.value)}
        status={fields[FieldNames.CONFIRM_PASSWORD].isValid === false ? 'error' : 'default'}
        error={fields[FieldNames.CONFIRM_PASSWORD].errorMsg}
        preventAutoComplete
        disabled={loading}
      />

      <div className='form__submit'>
        {serverErrMsg && <div className='form__submit__server-err'>{serverErrMsg}</div>}
        <Button
          id={SUBMIT_BTN_ID}
          size='md'
          color='primary'
          variant='contained'
          text={loading ? undefined : t('outerLayout.form.userActivation.createAccount')}
          loading={loading}
          disabled={!isFormValid(fields) || loading}
          onClickHandler={handleSubmit}
        />
      </div>
    </>
  )
}

export default UserActivationForm;